import React, { lazy, Suspense } from "react"
import "./index.css"
import { Layout } from "antd"
import { Route, Routes, useLocation } from "react-router-dom"
import { Content } from "antd/es/layout/layout"
import PrivateRoute from "./components/PrivateRoute"
import ManageMedia from "./pages/ManageMedia";

const Topbar = lazy(() => import("./components/dashboard/Topbar"))
const Sidebar = lazy(() => import("./components/dashboard/Sidebar"))
const AppFooter = lazy(() => import("./components/dashboard/Footer"))
const Dashboard = lazy(() => import("./pages/Dashboard"))
const School = lazy(() => import("./pages/School"))
const Projects = lazy(() => import("./pages/Projects"))
const Admin = lazy(() => import("./pages/Admin"))
const Auth = lazy(() => import("./pages/Auth"))
const NotFound = lazy(() => import("./pages/NotFound"))
const LandingPage = lazy(() => import("./pages/LandingPage"))
const Submissions = lazy(() => import("./pages/Submissions"))
const SchoolVerification = lazy(() => import("./components/school/SchoolVerification"))
const SchoolRegistrationPage = lazy(() => import("./pages/SchoolRegistrationPage"))
const ManageAppUsers = lazy(() => import("./pages/ManageAppUsers"))
const ManageActivities = lazy(() => import("./pages/ManageActivities"))
const SchoolLoginPage = lazy(() => import("./pages/SchoolLoginPage"))
const SchoolAdminDashboard = lazy(() => import("./pages/SchoolAdminDashboard"))
const ProjectView = lazy(() => import("./pages/ProjectView"))
const SchoolAdminSidebar = lazy(() => import("./components/school-admin/SchoolAdminSidebar"))
const ContactUs = lazy(() => import("./pages/ContactUs"))
const TermsOfService = lazy(() => import("./pages/TermsOfService"))
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"))
const GetStart = lazy(() => import("./pages/GetStart"))
const Whatsapp = lazy(() => import("./pages/Whatsapp"))
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"))
const PublicSubmissionView = lazy(() => import("./pages/PublicSubmissionView"))
const RegisterCompRow = lazy(() => import("./pages/RegisterCompRow"))
const CompetitionRegistrations = lazy(() => import("./pages/CompetitionRegistrations"))

const ADMIN_ROLES = ["admin", "superadmin", "manager"]
const PRINCIPAL_ROLES = ["principal"]

const LoadingSpinner = () => (
    <div
        style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
        }}
    >
        Loading...
    </div>
)

const PortalRoutes = React.memo(() => (
    <Routes>
        <Route path="/portal/login" element={<Auth />} />
        <Route
            path="/portal"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <Dashboard />
                </PrivateRoute>
            }
        />
        <Route
            path="/portal/dashboard"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <Dashboard />
                </PrivateRoute>
            }
        />
        <Route
            path="/portal/school"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <School />
                </PrivateRoute>
            }
        />
        <Route
            path="/portal/projects"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <Projects />
                </PrivateRoute>
            }
        />
        <Route
            path="/portal/submission"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <Submissions />
                </PrivateRoute>
            }
        />
        <Route
            path="/portal/pending-schools"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <SchoolVerification />
                </PrivateRoute>
            }
        />
        <Route
            path="/portal/app-users"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <ManageAppUsers />
                </PrivateRoute>
            }
        />
        <Route
            path="/portal/activities"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <ManageActivities />
                </PrivateRoute>
            }
        />
        <Route
            path="/portal/media"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <ManageMedia />
                </PrivateRoute>
            }
        />
        <Route
            path="/portal/whatsapp"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <Whatsapp />
                </PrivateRoute>
            }
        />
        <Route
            path="/portal/conversation"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <Whatsapp />
                </PrivateRoute>
            }
        />
        <Route
            path="/portal/admins"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <Admin />
                </PrivateRoute>
            }
        />
        <Route
            path="/portal/competition"
            element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                    <CompetitionRegistrations />
                </PrivateRoute>
            }
        />
        <Route path="/*" element={<NotFound />} />
    </Routes>
))

const SchoolAdminRoutes = React.memo(() => (
    <Routes>
        <Route
            path="/school-admin"
            element={
                <PrivateRoute allowedRoles={PRINCIPAL_ROLES}>
                    <SchoolAdminDashboard />
                </PrivateRoute>
            }
        />
        <Route
            path="/school-admin/project-view"
            element={
                <PrivateRoute allowedRoles={PRINCIPAL_ROLES}>
                    <ProjectView />
                </PrivateRoute>
            }
        />
        <Route path="/*" element={<NotFound />} />
    </Routes>
))

const PublicRoutes = React.memo(() => (
    <Routes>
        <Route path="/login" element={<SchoolLoginPage />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-service" element={<TermsOfService />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/get-start" element={<GetStart />} />
        <Route path="/register-comp-row" element={<RegisterCompRow />} />
        <Route path="/submission/:id" element={<PublicSubmissionView />} />
        <Route path="/*" element={<NotFound />} />
    </Routes>
))

function App() {
    const location = useLocation()

    const routeConfig = React.useMemo(() => {
        const path = location.pathname
        return {
            isLoginPage: path === "/portal/login",
            isLandingPage: path === "/",
            isPortalRoute: path.startsWith("/portal"),
            isRegistrationPage: path === "/school-registration",
            isSchoolAdminRoute: path.startsWith("/school-admin"),
        }
    }, [location.pathname])

    const { isLoginPage, isLandingPage, isPortalRoute, isRegistrationPage, isSchoolAdminRoute } = routeConfig

    return (
        <div className="App">
            <Suspense fallback={<LoadingSpinner />}>
                {isLandingPage ? (
                    <Routes>
                        <Route path="/" element={<LandingPage />} />
                    </Routes>
                ) : isPortalRoute ? (
                    <Layout style={{ minHeight: "100vh" }}>
                        {!isLoginPage && <Topbar />}
                        <Layout>
                            {!isLoginPage && <Sidebar />}
                            <Layout style={{ padding: "0 24px 24px" }}>
                                <Content
                                    className="site-layout-background"
                                    style={{
                                        padding: 24,
                                        margin: 0,
                                        minHeight: 280,
                                    }}
                                >
                                    <PortalRoutes />
                                </Content>
                                {!isLoginPage && <AppFooter />}
                            </Layout>
                        </Layout>
                    </Layout>
                ) : isRegistrationPage ? (
                    <Routes>
                        <Route path="/school-registration" element={<SchoolRegistrationPage />} />
                    </Routes>
                ) : isSchoolAdminRoute ? (
                    <Layout style={{ minHeight: "100vh" }}>
                        <SchoolAdminSidebar />
                        <Layout style={{ padding: "0 24px 24px" }}>
                            <Content
                                className="site-layout-background"
                                style={{
                                    padding: 24,
                                    margin: 0,
                                    minHeight: 280,
                                }}
                            >
                                <SchoolAdminRoutes />
                            </Content>
                        </Layout>
                    </Layout>
                ) : (
                    <PublicRoutes />
                )}
            </Suspense>
        </div>
    )
}

export default App
